import React, { useContext, useEffect, useState } from 'react'
import './index.scss'
import { Link } from 'react-router'
import { observer } from 'mobx-react'
import clsx from 'clsx'
import TagComponent from '@Components/Tag'
import Navigation from '@App/navigation'
import { StoresContext } from '@App/contexts'
import { WorkModel } from '@Entities/work/work'
import { AlternativeTitleModel } from '@Entities/work/alternative-title-model'
import { PublisherModel } from '@Entities/work/publisher-model'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'
import { WriterModel } from '@Entities/work/writer-model'
import { RecordingModel } from '@Entities/work/recording-model'
import WorkItemSimpleHeadInfo from '@Components/WorkItemSimple/WorkItemSimpleHeadInfo'
import WorkItemSimpleSubInfo from '@Components/WorkItemSimple/WorkItemSimpleSubInfo'

interface WorkItemSimpleProps {
    data: WorkModel
    valid: boolean
    rightsHolderId?: number
    disableView?: boolean
}

const getWorkWithLocalIds = (work: WorkModel): WorkModel => ({
    ...work,
    alternativeTitles: work.alternativeTitles
        ? work.alternativeTitles.map((at: AlternativeTitleModel, indexAT: number) => ({
              ...at,
              localId: indexAT + 1,
          }))
        : [],
    originalPublishers: work.originalPublishers
        ? work.originalPublishers.map((op: OriginalPublisherModel, indexOP: number) => ({
              ...op,
              localId: indexOP + 1,
              administratorPublishers: op.administratorPublishers
                  ? op.administratorPublishers.map((ap: PublisherModel, indexAP: number) => ({
                        ...ap,
                        localId: indexAP + 1,
                    }))
                  : [],
              writers: op.writers
                  ? op.writers.map((writer: WriterModel, indexWR: number) => ({
                        ...writer,
                        localId: indexWR + 1,
                    }))
                  : [],
          }))
        : [],
    writers: work.writers
        ? work.writers.map((writer: WriterModel, indexWR: number) => ({
              ...writer,
              localId: indexWR + 1,
          }))
        : [],
    recordings: work.recordings
        ? work.recordings.map((recording: RecordingModel, indexRES: number) => ({
              ...recording,
              localId: indexRES + 1,
          }))
        : [],
})

const WorkItemSimple = ({ data, valid, rightsHolderId, disableView }: WorkItemSimpleProps) => {
    const { workStore } = useContext(StoresContext)
    const [work, setWork] = useState<WorkModel | undefined>(undefined)

    const setWorkToDraft = () => {
        if (work) {
            workStore.setWorkDraftToLocal(work)
        }
    }

    useEffect(() => {
        setWork(getWorkWithLocalIds(data))
    }, [])

    if (!work) {
        return null
    }

    const {
        title,
        updateDate,
        rightsholderProprietaryId,
        iswc,
        isComplete, // isComplete === !typeDraft
        originalPublishers,
        writers,
        recordings,
        registrationHistoryIndicator,
        songCode,
    } = work

    let totalKnownShares = data.totalKnownShares
    totalKnownShares = totalKnownShares ? Math.round(totalKnownShares * 100) / 100 : 0

    let allPublishersCount: number = originalPublishers.length
    originalPublishers.length &&
        originalPublishers.forEach((op: OriginalPublisherModel) => {
            allPublishersCount += op.administratorPublishers.length
        })

    return (
        <div className={clsx('work-item-simple-component', valid ? 'valid' : 'invalid')}>
            <div className="item-head">
                <div className="title-view">
                    <div className="title">
                        <div className="name-block">
                            <h5 className="name" title={title}>
                                {title}
                            </h5>
                        </div>
                    </div>
                    <div className="right">
                        <span className={clsx('status small', valid ? 'valid' : 'invalid')}>
                            {valid ? 'Ready' : 'Error'}
                        </span>
                        {!disableView && (
                            <Link
                                to={Navigation.rightholder.createWork.bulk.edit.root(rightsHolderId as number)}
                                className="link bold"
                                onClick={setWorkToDraft}
                                onMouseDown={() => {}}
                            >
                                {valid ? 'View' : 'Resolve Errors'}
                            </Link>
                        )}
                    </div>
                </div>
                {isComplete && registrationHistoryIndicator && (
                    <div className="tags">
                        <TagComponent type={registrationHistoryIndicator} name={registrationHistoryIndicator} />
                    </div>
                )}
                <div className="head-sub-info">
                    <WorkItemSimpleHeadInfo
                        songCode={songCode}
                        iswc={iswc}
                        updateDate={updateDate}
                        rightsholderProprietaryId={rightsholderProprietaryId}
                        totalKnownShares={totalKnownShares}
                    />
                </div>
            </div>
            <WorkItemSimpleSubInfo
                writers={writers}
                originalPublishers={originalPublishers}
                allPublishersCount={allPublishersCount}
                recordings={recordings}
            />
        </div>
    )
}

export default observer(WorkItemSimple)
